import {
    Box,
    Checkbox,
    Chip,
    FormControl,
    InputLabel,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Select
} from "@mui/material";
import React from "react";
import { getFieldSx } from "../../util/style";
import { SmartWrapChips } from "./SmartWrapChips";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 400,
        },
    },
};

type DropDownMultipleFieldProps<Value> = {
    name: string;
    options: Value[];
    label: string;
    value: Value[] | undefined;
    getOptionLabel: (option: Value) => string;
    getOptionValue: (option: Value) => string;
    handleValueChange: (value: any, field?: string) => void,
    handleValidation: (value: any, field?: string) => void | boolean,
    error?: string | undefined;
};

export function DropDownMultipleField<Value>({
                                                 name,
                                                 error,
                                                 options,
                                                 handleValueChange,
                                                 handleValidation,
                                                 value,
                                                 label,
                                                 getOptionLabel,
                                                 getOptionValue,
                                             }: DropDownMultipleFieldProps<Value>) {
    const extractedValueIds: string[] = !!value && value.length > 0 ? value.map(item => getOptionValue(item)) : [];

    const mapIdentifierToValue = (identifier: string): Value | null => {
        const value = options.filter(option => identifier === getOptionValue(option));
        return !!value && value.length === 1 ? value[0] : null;
    }

    const onChange = (updatedIds: string[]) => {
        const updatedValue: Value[] = updatedIds.map(id => mapIdentifierToValue(id)).filter(option => option !== null) as Value[];

        handleValueChange(updatedValue, name);
        handleValidation(updatedValue as any, name);
    };

    return (
      <FormControl error={!!error} sx={getFieldSx(name, error)}>
          <InputLabel sx={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: 'calc(100% - 45px)',

              '&.MuiInputLabel-shrink': {
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '140%',
              }
          }}>{label}</InputLabel>
          <Select
            multiple
            value={extractedValueIds}
            onChange={event => {
                onChange(event.target.value as string[])
            }}
            input={<OutlinedInput label={label} />}
            error={!!error}
            slotProps={
                {
                    input: {
                        sx: {
                            '&.MuiInputLabel-shrink': {
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: '120%',
                            },
                        },
                    }
                }
            }
            renderValue={(selected) => (
                <SmartWrapChips
                    selected={selected}
                    getLabel={(id) => {
                        const val = mapIdentifierToValue(id);
                        return val ? getOptionLabel(val) : "";
                    }}
                />
            )}
            MenuProps={MenuProps}
          >
              {options.map((option, index) => (
                <MenuItem
                  sx={{
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      '&.Mui-selected': {
                          backgroundColor: '#EAF5E6',
                          color: '#52AE30',
                          '&:hover': {
                              backgroundColor: '#E5F0E1',
                          },
                      },
                  }}
                  key={index}
                  selected={extractedValueIds.includes(getOptionValue(option))}
                  value={getOptionValue(option)}>
                    <Checkbox
                      sx={{
                          '&.Mui-checked': {
                              color: '#52AE30',
                          },
                      }}
                      checked={extractedValueIds.includes(getOptionValue(option))}
                    />
                    <ListItemText
                      primary={getOptionLabel(option)}
                      sx={{
                          '&.Mui-checked': {
                              color: '#52AE30',
                          },
                      }}
                    />
                </MenuItem>
              ))}
          </Select>
          {error && <p className="text-xs text-red-500 mt-1 ml-1">{error}</p>}
      </FormControl>
    )
}
